import * as TableauUI from '@tableau/tableau-ui';
import * as React from 'react';
import { makeTestId } from '../../utils/Declarative';
import { ButtonAttrs } from '../../utils/HtmlAttrs';
import { EventUtilsOptOut } from '../../utils/ReactHelpers';

export interface MenuButtonProps {
  /**
   * Set this prop to the DOM `id` of the popup element with `[role="menu"]`.
   */
  'aria-controls': string;

  /**
   * Set this prop to `true` if the popup does not follow the WAI-ARIA `menu` role pattern:
   * https://www.w3.org/TR/wai-aria-practices-1.1/#menu.
   * @default 'menu'
   */
  'aria-haspopup'?: 'menu' | true;

  /**
   * Medium- and high-density buttons have less padding in order to fit into tighter spaces.
   * @default '"high"'
   */
  density?: 'low' | 'medium' | 'high';

  kind?: 'outline' | 'primary' | 'outlineDark';

  isOpen?: boolean;

  /** Attach a handler which displays a popup menu here, and toggle the `isOpen` prop in the same handler. */
  onClick?: React.MouseEventHandler<HTMLButtonElement>;

  /** Test id to be attached to the button. '-MenuButton' will be automatically added as a suffix. */
  testId: string;
}

/**
 * An Outline-style button with an arrow indicating the availability of a popup menu, along with WAI-ARIA attributes
 * as specified at https://www.w3.org/TR/wai-aria-practices-1.1/#menubutton.
 *
 * The display of e.g. a dropdown menu is intentionally left to the code consuming the widget.
 */
export const MenuButtonWidget = React.forwardRef<HTMLButtonElement, MenuButtonProps & ButtonAttrs>((props, ref) => {
  const { isOpen, testId, ...buttonProps } = props;

  return (
    <TableauUI.Button
      {...EventUtilsOptOut}
      aria-haspopup='menu'
      kind='outline'
      {...buttonProps}
      aria-controls={isOpen ? buttonProps['aria-controls'] : undefined}
      aria-expanded={isOpen}
      data-tb-test-id={makeTestId(testId, 'MenuButton')}
      onKeyDown={e => {
        if (e.key === 'ArrowDown' && props.onClick) {
          e.preventDefault();
          props.onClick(e as unknown as React.MouseEvent<HTMLButtonElement>);
        }
        if (props.onKeyDown) {
          props.onKeyDown(e);
        }
      }}
      ref={ref}
    />
  );
});
MenuButtonWidget.displayName = 'MenuButtonWidget';
MenuButtonWidget.defaultProps = { 'aria-haspopup': 'menu', density: 'high' };
